import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext({});

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Fetch user profile from backend
  const fetchUserProfile = useCallback(async (token) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/auth/user/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Fetch user profile if logged in
      if (session?.access_token) {
        fetchUserProfile(session.access_token);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Fetch user profile on login, clear on logout
      if (session?.access_token) {
        fetchUserProfile(session.access_token);
      } else {
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserProfile]);

  const loginWithGoogle = async () => {
    // Use production URL for OAuth redirect to show custom domain in Google consent screen
    // Falls back to current origin for local development
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${siteUrl}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    return { error };
  };

  const signInWithEmail = async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password });
  };

  const signUpWithEmail = async (email, password) => {
    return await supabase.auth.signUp({ email, password });
  };

  const logout = async () => {
    return await supabase.auth.signOut();
  };

  const getToken = () => {
    const token = session?.access_token;
    if (!token) {
      console.warn('No token available in session');
    }
    return token;
  };

  // Refresh user profile (call after updating profile data)
  const refreshUserProfile = useCallback(() => {
    if (session?.access_token) {
      fetchUserProfile(session.access_token);
    }
  }, [session?.access_token, fetchUserProfile]);

  const value = {
    user,
    session,
    userProfile,
    loginWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    loading,
    getToken,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};